import { Easing, interpolate } from "remotion";
import type { Cam } from "../kit/MapStage";

// The Stippl map is the stage. Real coordinates: stops from the trip, hotels from the KB places
// (stippl-knowledge explore/places/thailand/*.json) and Nuitee (explore/raw_places, La Mai).

export type LL = [number, number];
export const STOP_LL: LL[] = [
	[100.5018, 13.7563], // Bangkok
	[98.9853, 18.7883], // Chiang Mai
	[98.4403, 19.3583], // Pai
];

// the three picks on the Bangkok stop card, in PICK_CARDS order
export const PICK_LL: LL[] = [
	[100.5781565, 13.7268055], // Volve Hotel Bangkok (Thonglor)
	[100.585062, 13.68043], // Bangkok Tree House (Bang Krachao)
	[100.50473, 13.7570091], // Old Capital Bike Inn (old town)
];

// price pins of the results map (R1), each on a real stay in Chiang Mai. The prices are the
// design's example prices; La Mai is the selected pin, as on the sheet.
export const PIN_LL: Record<string, LL> = {
	rsel: [99.006151, 18.763801], // La Mai Hotel Chiangmai (Nuitee)
	rpin1: [98.981036, 18.7905342], // Baan Boo Loo
	rpin6: [98.9897558, 18.7885472], // Tamarind Village
	rpin0: [99.0041676, 18.7919289], // 137 Pillars House
	rpin4: [98.9837787, 18.7959725], // About A Bed Hostel
	rpin3: [98.9875049, 18.7822652], // Phor Liang Meun Terracotta Arts Hotel
	rpin7: [99.0042008, 18.7796266], // Maladee Rendezvous Hotel
	rpin8: [98.9940118, 18.7925887], // Stamps Backpackers
	rpin2: [99.0028782, 18.7911939], // sala lanna
	rpin9: [98.99127, 18.78463], // La Maison Verte Hostel
};

type Pad = { top: number; bottom: number; left: number; right: number };
type Key = { f: number; lng: number; lat: number; zoom: number; pitch: number; bearing: number; hop?: number; pad: [Pad, Pad] };

const P = (l: Partial<Pad>, p: Partial<Pad>): [Pad, Pad] => [
	{ top: 0, bottom: 0, left: 0, right: 0, ...l },
	{ top: 0, bottom: 0, left: 0, right: 0, ...p },
];
const TEXT = P({ left: 760 }, { top: 560 });

export const KEYS: Key[] = [
	{ f: 0, lng: 99.6, lat: 16.3, zoom: 5.9, pitch: 26, bearing: 36, pad: TEXT },
	{ f: 214, lng: 99.47, lat: 16.56, zoom: 6.6, pitch: 30, bearing: 48, pad: TEXT },
	{ f: 254, lng: 100.552, lat: 13.722, zoom: 11.7, pitch: 50, bearing: 22, pad: TEXT },
	{ f: 356, lng: 100.552, lat: 13.722, zoom: 11.95, pitch: 52, bearing: 30, pad: TEXT },
	{ f: 450, lng: 98.996, lat: 18.779, zoom: 13.25, pitch: 55, bearing: -14, hop: 6.8, pad: TEXT },
	{ f: 800, lng: 98.996, lat: 18.779, zoom: 13.6, pitch: 57, bearing: 4, pad: TEXT },
	{ f: 852, lng: 99.47, lat: 16.56, zoom: 6.15, pitch: 30, bearing: 48, pad: P({ left: 900, right: 560 }, { top: 480, bottom: 1000 }) },
	{ f: 900, lng: 99.47, lat: 16.56, zoom: 6.25, pitch: 30, bearing: 44, pad: P({ left: 900, right: 560 }, { top: 480, bottom: 1000 }) },
	{ f: 968, lng: 62, lat: 27, zoom: 1.95, pitch: 0, bearing: 0, pad: P({ left: 860 }, { top: 760 }) },
	{ f: 1020, lng: 67, lat: 27, zoom: 1.9, pitch: 0, bearing: 0, pad: P({ left: 860 }, { top: 760 }) },
];

const eio = Easing.bezier(0.65, 0, 0.35, 1);
const mix = (t: number, a: number, b: number) => a + (b - a) * t;

export const camAt = (f: number, land: boolean): Cam & { padding: Pad } => {
	let i = 0;
	while (i < KEYS.length - 2 && f >= KEYS[i + 1].f) i++;
	const a = KEYS[i];
	const b = KEYS[i + 1];
	const t = interpolate(f, [a.f, b.f], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: eio });
	// zooming in: arrive over the target early; zooming out: leave it late
	const zin = b.zoom > a.zoom + 2;
	const zout = a.zoom > b.zoom + 2;
	const tc = zin ? Math.pow(t, 0.55) : zout ? Math.pow(t, 1.7) : t;
	let zoom = mix(t, a.zoom, b.zoom);
	if (b.hop) zoom -= Math.max(0, mix(0.5, a.zoom, b.zoom) - b.hop) * Math.sin(Math.PI * t);
	// high up, look down: a tilted camera at low zoom loads tiles to the horizon (and crashed the renderer)
	const pitchDip = b.hop ? 38 * Math.sin(Math.PI * t) : 0;
	const pa = a.pad[land ? 0 : 1];
	const pb = b.pad[land ? 0 : 1];
	return {
		lng: mix(tc, a.lng, b.lng),
		lat: mix(tc, a.lat, b.lat),
		zoom,
		pitch: mix(t, a.pitch, b.pitch) - pitchDip,
		bearing: mix(t, a.bearing, b.bearing),
		padding: { top: mix(t, pa.top, pb.top), bottom: mix(t, pa.bottom, pb.bottom), left: mix(t, pa.left, pb.left), right: mix(t, pa.right, pb.right) },
	};
};

// the route in geographic space: a gentle quadratic bend per leg, sampled
export const routeGeo = (samples = 48): LL[][] =>
	[0, 1].map((s) => {
		const [x0, y0] = STOP_LL[s];
		const [x1, y1] = STOP_LL[s + 1];
		const dx = x1 - x0;
		const dy = y1 - y0;
		const side = s === 0 ? 1 : -1;
		const cx = (x0 + x1) / 2 + -dy * 0.18 * side;
		const cy = (y0 + y1) / 2 + dx * 0.18 * side;
		return Array.from({ length: samples + 1 }, (_, j) => {
			const u = j / samples;
			return [(1 - u) * (1 - u) * x0 + 2 * (1 - u) * u * cx + u * u * x1, (1 - u) * (1 - u) * y0 + 2 * (1 - u) * u * cy + u * u * y1] as LL;
		});
	});

// 34 s at 30 fps: the film on its own clock. It ships slowed, 1066 frames (see ./BookingIntroSlow.tsx)
export const BOOKING_FPS = 30;
export const BOOKING_FRAMES = 1020;
